import type {
  DegrauDoFunil,
  EtapaDaPessoa,
  EtapaDoFunil,
  PorqueLeu,
  SituacaoDaPessoa,
} from '@titularis/api-client';

/**
 * O vocabulário da seção de Pessoas, num lugar só.
 *
 * O código e as rotas falam inglês; a tela fala a língua do Calebe e do Róger
 * (G-CANONICAL). Aqui, e não espalhado pelas quatro telas da seção, porque a
 * etapa aparece em três delas - no cartão do funil, na coluna da tabela e no
 * cabeçalho da pessoa - e escrita três vezes ganharia três nomes.
 *
 * **Os rótulos dizem o que a pessoa fez ou deixou de fazer**, e não o nome
 * interno do estado: quem olha esta tela está decidindo a quem escrever hoje, e
 * "Não confirmou o e-mail" responde isso enquanto "sem_confirmacao" só descreve.
 */
export const ETAPA_LABEL: Record<EtapaDaPessoa, string> = {
  sem_confirmacao: 'Não confirmou o e-mail',
  sem_uso: 'Ainda não estudou',
  estudando: 'Estudando',
  no_limite: 'Bateu no limite',
  comprou: 'Comprou',
};

/**
 * A cor de cada etapa, e ela conta uma história.
 *
 * Vermelho não aparece: nenhuma destas etapas é erro, e pintar de vermelho quem
 * não confirmou o e-mail faria a tela acusar a pessoa de algo que provavelmente
 * é culpa da nossa caixa de saída. O laranja do "bateu no limite" é o único
 * quente, e ele é **bom**: é o sinal de compra mais forte que a base tem.
 */
export const ETAPA_COR: Record<EtapaDaPessoa, string> = {
  sem_confirmacao: 'default',
  sem_uso: 'default',
  estudando: 'blue',
  no_limite: 'orange',
  comprou: 'green',
};

/** O que fazer com quem está nesta etapa. Vira a dica do balão. */
export const ETAPA_AJUDA: Record<EtapaDaPessoa, string> = {
  sem_confirmacao:
    'A conta existe e não vale nada ainda: sem o e-mail confirmado ela não responde questão. Reenviar a confirmação é a ação desta linha.',
  sem_uso: 'Confirmou e nunca abriu nada. É quem o primeiro e-mail de retorno tem de alcançar.',
  estudando: 'Está usando o que o plano gratuito dá, e ainda não chegou ao fim dele.',
  no_limite:
    'Gastou a cota da semana e continuou aqui. É o momento em que a oferta faz mais sentido.',
  comprou: 'Pagou. O que importa agora é se está usando o que comprou.',
};

export const SITUACAO_LABEL: Record<SituacaoDaPessoa, string> = {
  backoffice: 'Equipe',
  assinante: 'Assinante',
  vencido: 'Acesso vencido',
  gratuito: 'Gratuito',
};

export const SITUACAO_COR: Record<SituacaoDaPessoa, string> = {
  backoffice: 'purple',
  assinante: 'green',
  // Vermelho aqui é o certo: um acesso que venceu é dinheiro que parou de entrar.
  vencido: 'red',
  gratuito: 'default',
};

/**
 * A conta de teste: papel de aluno, e fora de toda métrica.
 *
 * Cinza-azulado (`cyan`), e não o roxo da equipe: é outra coisa. A equipe tem
 * poder no backoffice; a conta de teste é um aluno de mentira, e a etiqueta
 * existe para ninguém escrever para ela achando que é gente.
 */
export const CONTA_DE_TESTE_LABEL = 'Conta de teste';
export const CONTA_DE_TESTE_COR = 'cyan';
export const CONTA_DE_TESTE_AJUDA =
  'Marcada como conta de teste: fica fora do funil, da aba Origem e, por padrão, da lista.';

/** O nome de cada degrau do funil, na ordem em que a API os devolve. */
export const DEGRAU_LABEL: Record<EtapaDoFunil, string> = {
  inscritos: 'Na lista',
  contas: 'Criaram conta',
  confirmaram: 'Confirmaram',
  estudaram: 'Estudaram',
  no_limite: 'No limite',
  pediram: 'Abriram checkout',
  compraram: 'Compraram',
};

export const DEGRAU_AJUDA: Record<EtapaDoFunil, string> = {
  inscritos:
    'Quem pediu para ser avisado e não se descadastrou, sem as contas internas. Em geral não tem conta: é um caminho paralelo, e não o topo desta escada - por isso a conversão não é contada a partir dele. A aba Listas de espera mostra quem são.',
  contas:
    'Contas de aluno criadas. As da equipe e as marcadas como teste ficam de fora de todo o funil.',
  confirmaram: 'Confirmaram o e-mail. Quem não confirma não consegue responder questão nenhuma.',
  estudaram: 'Responderam, abriram uma resolução ou leram um artigo de lei, ao menos uma vez.',
  no_limite:
    'Gastaram a cota gratuita desta semana e não têm acesso pago. É um retrato de agora, e não um degrau por onde todo mundo passa - por isso fica fora da conta de passagem.',
  pediram:
    'Chegaram ao checkout, tendo pago ou não. A distância entre este degrau e o próximo é a que diz se o problema é a oferta ou o pagamento. A passagem pode superar 100%: comprar não exige ter estudado antes.',
  compraram: 'Pagaram de verdade. Compra de ensaio, do sandbox, não entra em degrau nenhum.',
};

/**
 * Os degraus que **não** entram na escada de conversão.
 *
 * `inscritos` fica de fora porque quem entra na lista **não** costuma ter conta:
 * dividir `contas` por ele produziria uma taxa sobre populações diferentes - o
 * erro de leitura mais caro que um funil pode ter, porque o número sai
 * plausível.
 *
 * `no_limite` fica de fora por um motivo diferente e igualmente importante: os
 * outros degraus contam **quem já passou por ali alguma vez**, e este conta
 * **quem está ali agora**. Quem bateu no limite e comprou na semana seguinte
 * saiu deste degrau e continua em todos os outros. Encadeado, ele produzia a
 * leitura absurda que o navegador mostrou em 18/09/2026: três pessoas
 * estudaram, **zero** estão no limite, e sete abriram checkout - uma escada com
 * um degrau vazio no meio, que se lê como defeito e é a régua errada.
 *
 * Fora da escada, ele continua na faixa e continua sendo o sinal mais forte que
 * a base dá; o que ele deixa de fazer é fingir que é etapa de passagem.
 */
const FORA_DA_ESCADA: EtapaDoFunil[] = ['inscritos', 'no_limite'];

/** Esse degrau é um estado de agora, e não um degrau da escada? */
export const foraDaEscada = (etapa: EtapaDoFunil): boolean => FORA_DA_ESCADA.includes(etapa);

export interface DegrauNaTela extends DegrauDoFunil {
  /** Quanto do degrau anterior chegou aqui. Nulo no primeiro e sem denominador. */
  conversao: number | null;
}

/**
 * Prepara os degraus para a tela, calculando a passagem entre eles.
 *
 * **O denominador é o degrau anterior da escada, e não o primeiro de todos.** A
 * pergunta que se faz olhando um funil é "onde as pessoas param", e essa é uma
 * pergunta sobre cada passagem; a taxa contra o topo responde outra coisa, e
 * lida como se fosse esta faz o último degrau parecer sempre o pior.
 *
 * Zero no anterior devolve `null`, e não zero: sem denominador não há taxa, e
 * "0%" afirmaria uma queda que ninguém mediu.
 */
export function comConversao(degraus: DegrauDoFunil[]): DegrauNaTela[] {
  let anterior: number | null = null;

  return degraus.map((degrau) => {
    if (FORA_DA_ESCADA.includes(degrau.etapa)) return { ...degrau, conversao: null };

    const conversao = anterior === null || anterior === 0 ? null : degrau.total / anterior;
    anterior = degrau.total;
    return { ...degrau, conversao };
  });
}

const formatoDaTaxa = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  maximumFractionDigits: 0,
});

export const formatarTaxa = (taxa: number | null): string | null =>
  taxa === null ? null : formatoDaTaxa.format(taxa);

/** A taxa de acerto de quem respondeu. Sem resposta não há taxa. */
export function taxaDeAcerto(respostas: number, acertos: number): number | null {
  return respostas === 0 ? null : acertos / respostas;
}

/**
 * Há quanto tempo, em palavras curtas.
 *
 * A tabela mostra "ontem" e "há 3 dias" em vez da data cheia porque a pergunta
 * é de recência, não de calendário: quem abriu a tela quer separar quem sumiu
 * de quem está aqui agora, e `17/09/2026 14:32` obriga a fazer a subtração de
 * cabeça, linha por linha. A data exata continua no balão.
 */
export function haQuantoTempo(iso: string | null | undefined, agora = new Date()): string {
  if (!iso) return 'nunca';

  const quando = new Date(iso);
  const minutos = Math.floor((agora.getTime() - quando.getTime()) / 60000);

  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas} h`;

  const dias = Math.floor(horas / 24);
  if (dias === 1) return 'ontem';
  if (dias < 30) return `há ${dias} dias`;

  const meses = Math.floor(dias / 30);
  return meses === 1 ? 'há 1 mês' : `há ${meses} meses`;
}

/**
 * Por que esta conta pôde ler o comentário desta lei.
 *
 * **Existe porque a ausência dela produziu um alarme legítimo em 19/09/2026:**
 * cinco comentários do Provimento CNJ 149/2023 na ficha de uma conta gratuita
 * leem-se como furo de acesso, e não eram - eram a degustação semanal fazendo
 * exatamente o que a `canReadCommentary` manda. Mostrar o fato sem a autorização
 * é o pior jeito de mostrar um fato.
 *
 * A ordem dos três repete a da política: a gratuidade da lei vence o direito, e
 * o direito vence a degustação.
 */
export const PORQUE_LABEL: Record<PorqueLeu, string> = {
  lei_gratuita: 'Lei aberta',
  direito: 'Tem o produto',
  degustacao: 'Pela degustação',
};

export const PORQUE_COR: Record<PorqueLeu, string> = {
  lei_gratuita: 'blue',
  direito: 'green',
  // Nem verde nem vermelho: não é compra, e não é irregularidade.
  degustacao: 'default',
};

export const PORQUE_AJUDA: Record<PorqueLeu, string> = {
  lei_gratuita:
    'A casa abriu esta lei para todo mundo, e ler não gasta cota nenhuma.',
  direito: 'Comprou ou recebeu este produto, e por isso lê sem gastar a degustação.',
  degustacao:
    'Não tem este produto, e leu pela cota semanal de comentários de lei - o número da tela de Acesso. É a amostra que existe para a Legislação Comentada poder ser vista antes de ser comprada (D-42, D-54). Zerar a cota naquela tela fecha a amostra.',
};

/** O nome do provedor de entrada, como quem atende suporte fala dele. */
export const ENTRADA_LABEL: Record<string, string> = {
  credential: 'E-mail e senha',
  google: 'Google',
};
